import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { DollarSign, Activity, TrendingUp, Zap, ChevronLeft, ChevronRight, RefreshCw, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BILLING_URL = process.env.REACT_APP_BILLING_URL || '';
const PAGE_SIZE = 20;

const ROUTE_LABELS = {
  'api/v2/envolvido/processos/cpf_cnpj={cpf_cnj}': 'CPF / CNPJ',
  'api/v2/processos/numero_cnj/{cnj}': 'Busca CNJ',
  'api/v2/processos/numero_cnj/{cnj}/movimentacoes': 'Movimentações',
};

const formatBRL = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const getDefaultDates = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
};

const getAuthHeader = () => {
  try {
    const user = JSON.parse(localStorage.getItem('juridic_user') || '{}');
    return user.token ? `Bearer ${user.token}` : null;
  } catch {
    return null;
  }
};

export default function Consumo() {
  const { darkMode, currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [filterUsername, setFilterUsername] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminCompanies, setAdminCompanies] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    const auth = getAuthHeader();
    const headers = auth ? { 'Authorization': auth } : {};
    Promise.all([
      fetch(`${BILLING_URL}/admin/users`, { headers }).then(r => r.json()),
      fetch(`${BILLING_URL}/admin/companies`, { headers }).then(r => r.json()),
    ]).then(([users, companies]) => {
      setAdminUsers(Array.isArray(users) ? users : []);
      setAdminCompanies(Array.isArray(companies) ? companies : []);
    }).catch(() => {});
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBilling = async (p, start, end, username, company) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: p, limit: PAGE_SIZE });
      if (start) params.set('start', start);
      if (end) params.set('end', end);
      if (username) params.set('username', username);
      if (company) params.set('company', company);
      const auth = getAuthHeader();
      const res = await fetch(`${BILLING_URL}/billing?${params}`, {
        headers: auth ? { 'Authorization': auth } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBilling(1, defaults.start, defaults.end, '', '');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFiltrar = () => {
    setPage(1);
    fetchBilling(1, startDate, endDate, filterUsername, filterCompany);
  };

  const handlePage = (p) => {
    setPage(p);
    fetchBilling(p, startDate, endDate, filterUsername, filterCompany);
  };

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  const card = (icon, label, value, sub, color) => (
    <div className={`rounded-xl border p-5 ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center justify-between mb-3">
        <p className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-neutral-500' : 'text-gray-500'}`}>{label}</p>
        <div className={`p-2 rounded-lg ${color}`}>{icon}</div>
      </div>
      <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{value}</p>
      {sub && <p className={`text-xs mt-1 ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className={`text-2xl font-bold flex items-center gap-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <DollarSign className="w-8 h-8" />
          Consumo
        </h2>
        <p className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
          Histórico de requisições e custos na API Escavador
        </p>
      </div>

      {/* Filtros */}
      <div className={`rounded-xl border p-4 flex flex-wrap items-end gap-3 ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'}`}>
        <div className="flex flex-col gap-1">
          <label className={`text-xs font-medium ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>De</label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-primary-500/30`}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={`text-xs font-medium ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>Até</label>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-primary-500/30`}
          />
        </div>
        {isAdmin && (
          <>
            <div className="flex flex-col gap-1">
              <label className={`text-xs font-medium ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>Usuário</label>
              <select
                value={filterUsername}
                onChange={e => setFilterUsername(e.target.value)}
                className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-primary-500/30`}
              >
                <option value="">Todos</option>
                {adminUsers.map(u => (
                  <option key={u._id} value={u.user}>{u.user}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className={`text-xs font-medium ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>Empresa</label>
              <select
                value={filterCompany}
                onChange={e => setFilterCompany(e.target.value)}
                className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-primary-500/30`}
              >
                <option value="">Todas</option>
                {adminCompanies.map(c => (
                  <option key={c._id} value={c.company}>{c.company}</option>
                ))}
              </select>
            </div>
          </>
        )}
        <button
          onClick={handleFiltrar}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50 transition-all"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Aplicar
        </button>
      </div>

      {/* Erro */}
      {error && (
        <div className={`flex items-center gap-3 p-4 rounded-xl border ${darkMode ? 'bg-red-900/20 border-red-800/50 text-red-400' : 'bg-red-50 border-red-200 text-red-700'}`}>
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">Não foi possível conectar ao servidor de billing: <span className="font-mono">{error}</span></p>
        </div>
      )}

      {/* Cards */}
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {card(
              <DollarSign className="w-4 h-4 text-green-600" />, 'Total Gasto',
              formatBRL(data.totalValue), `${data.count} requisições`,
              darkMode ? 'bg-green-900/30' : 'bg-green-100'
            )}
            {card(
              <Activity className="w-4 h-4 text-blue-600" />, 'Total Requisições',
              data.count.toLocaleString('pt-BR'), `${data.dailyData?.length || 0} dias com uso`,
              darkMode ? 'bg-blue-900/30' : 'bg-blue-100'
            )}
            {card(
              <TrendingUp className="w-4 h-4 text-purple-600" />, 'Custo Médio / Dia',
              data.dailyData?.length
                ? formatBRL(data.totalValue / data.dailyData.length)
                : 'R$ 0,00',
              'no período selecionado',
              darkMode ? 'bg-purple-900/30' : 'bg-purple-100'
            )}
            {card(
              <Zap className="w-4 h-4 text-amber-600" />, 'Rota Mais Usada',
              data.routeData?.[0]
                ? (ROUTE_LABELS[data.routeData[0].route] || data.routeData[0].route)
                : '-',
              data.routeData?.[0] ? `${data.routeData[0].count} chamadas` : '',
              darkMode ? 'bg-amber-900/30' : 'bg-amber-100'
            )}
          </div>

          {/* Gráfico diário */}
          {data.dailyData?.length > 0 && (
            <div className={`rounded-xl border p-5 ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'}`}>
              <p className={`text-sm font-semibold mb-4 ${darkMode ? 'text-neutral-300' : 'text-gray-700'}`}>Custo por dia</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.dailyData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#262626' : '#f0f0f0'} vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: darkMode ? '#737373' : '#9ca3af' }} axisLine={false} tickLine={false}
                    tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11, fill: darkMode ? '#737373' : '#9ca3af' }} axisLine={false} tickLine={false}
                    tickFormatter={v => `R$${v.toFixed(2)}`} />
                  <Tooltip
                    contentStyle={{ background: darkMode ? '#171717' : '#fff', border: `1px solid ${darkMode ? '#404040' : '#e5e7eb'}`, borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: darkMode ? '#d4d4d4' : '#374151', fontWeight: 600 }}
                    formatter={(v, _n, { payload }) => [`${formatBRL(v)} (${payload.count} req.)`, 'Custo']}
                  />
                  <Bar dataKey="total" fill={darkMode ? '#be123c' : '#cc092f'} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Breakdown por rota */}
          {data.routeData?.length > 0 && (
            <div className={`rounded-xl border p-5 ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'}`}>
              <p className={`text-sm font-semibold mb-4 ${darkMode ? 'text-neutral-300' : 'text-gray-700'}`}>Consumo por rota</p>
              <div className="space-y-3">
                {data.routeData.map(r => {
                  const pct = data.count > 0 ? (r.count / data.count) * 100 : 0;
                  return (
                    <div key={r.route}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium ${darkMode ? 'text-neutral-300' : 'text-gray-700'}`}>
                          {ROUTE_LABELS[r.route] || r.route}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>{r.count} req.</span>
                          <span className={`text-sm font-semibold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{formatBRL(r.total)}</span>
                        </div>
                      </div>
                      <div className={`h-1.5 rounded-full ${darkMode ? 'bg-neutral-800' : 'bg-gray-100'}`}>
                        <div className="h-1.5 rounded-full bg-primary-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tabela */}
          <div className={`rounded-xl border overflow-hidden ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'}`}>
            <div className={`px-5 py-4 border-b ${darkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <p className={`text-sm font-semibold ${darkMode ? 'text-neutral-300' : 'text-gray-700'}`}>
                  Registros
                </p>
                <span className={`text-xs px-2 py-1 rounded ${darkMode ? 'bg-neutral-800 text-neutral-400' : 'bg-gray-100 text-gray-600'}`}>
                  {data.total.toLocaleString('pt-BR')} total
                </span>
              </div>
            </div>

            {data.items.length === 0 ? (
              <p className={`text-center py-12 text-sm ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>
                Nenhum registro no período selecionado
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${darkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                        {['Data', 'Hora', ...(isAdmin ? ['Usuário'] : []), 'Rota', 'Tipo', 'Parâmetro', 'Valor'].map(h => (
                          <th key={h} className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((item, i) => (
                        <tr key={i} className={`border-b last:border-0 transition-colors ${darkMode ? 'border-neutral-800 hover:bg-neutral-800/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                          <td className={`px-5 py-3 font-mono text-xs ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                            {item.datetime?.split(' ')[0] ?? '-'}
                          </td>
                          <td className={`px-5 py-3 font-mono text-xs ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                            {item.datetime?.split(' ')[1] ?? '-'}
                          </td>
                          {isAdmin && (
                            <td className={`px-5 py-3 text-xs ${darkMode ? 'text-neutral-300' : 'text-gray-700'}`}>
                              {item.username || '-'}
                            </td>
                          )}
                          <td className={`px-5 py-3 ${darkMode ? 'text-neutral-300' : 'text-gray-700'}`}>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-neutral-800 text-neutral-300' : 'bg-gray-100 text-gray-600'}`}>
                              {ROUTE_LABELS[item.route] || item.route}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            {item.type ? (
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
                                item.type === 'cpf'
                                  ? (darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700')
                                  : item.type === 'cnpj'
                                  ? (darkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-700')
                                  : (darkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700')
                              }`}>
                                {item.type}
                              </span>
                            ) : '-'}
                          </td>
                          <td className={`px-5 py-3 font-mono text-xs ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                            {item.param || '-'}
                          </td>
                          <td className={`px-5 py-3 font-semibold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                            {formatBRL(item.value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                  <div className={`flex items-center justify-between px-5 py-3 border-t ${darkMode ? 'border-neutral-800 bg-neutral-800/30' : 'border-gray-200 bg-gray-50'}`}>
                    <span className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                      Página {page} de {totalPages}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePage(page - 1)}
                        disabled={page === 1 || loading}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed ${darkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-300 hover:bg-neutral-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                      >
                        <ChevronLeft className="w-4 h-4" /> Anterior
                      </button>
                      <button
                        onClick={() => handlePage(page + 1)}
                        disabled={page === totalPages || loading}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed ${darkMode ? 'bg-neutral-800 border-neutral-700 text-neutral-300 hover:bg-neutral-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                      >
                        Próxima <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Loading sem dados ainda */}
      {loading && !data && (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className={`w-8 h-8 animate-spin ${darkMode ? 'text-neutral-600' : 'text-gray-400'}`} />
        </div>
      )}
    </div>
  );
}
